<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import { helpTL } from "../../generated/tl";
import { useFlash } from "mu-vue-utils/Composables/flash";
import { formatTlError } from "../../services/tlError";
import type { WMModal } from "../../composables/WM";
import Loader from "../Loader.vue";
import HelpMenuTree from "./HelpMenuTree.vue";
import type { HelpIndexNode } from "../../types/help";

const props = defineProps<{ modal: WMModal, initialFile?: string }>();

const { addFlash } = useFlash();

const content = ref<HelpIndexNode[]>([]);
const selectedFile = ref<string | null>(null);
const html = ref("");
const loadingIndex = ref(true);
const loadingPage = ref(false);
const pageCache = new Map<string, string>();
const contentEl = ref<HTMLElement | null>(null);

// Cíl odkazu v nápovědě: `soubor.md`, `soubor.md@kotva` nebo `@kotva` (kotva na stejné stránce).
// Oddělovač je `@`, protože `#` už používá `#help:...` v URL. Kotva = `id` elementu na stránce
// (v markdownu `## Nadpis {#kotva}`, viz HelpService::createConverter()).
const parseTarget = (target: string): { file: string | null, anchor: string | null } => {
	const at = target.indexOf("@");
	if (at === -1) return { file: target || null, anchor: null };
	return { file: target.slice(0, at) || null, anchor: target.slice(at + 1) || null };
};

const scrollToAnchor = async (anchor: string) => {
	await nextTick(); // obsah stránky se po načtení vykresluje až v dalším ticku
	// hledá se jen uvnitř obsahu nápovědy, ať se nechytí stejné id odjinud v aplikaci
	contentEl.value?.querySelector(`[id="${CSS.escape(anchor)}"]`)?.scrollIntoView({ block: "start" });
};

const openTarget = async (target: string) => {
	const { file, anchor } = parseTarget(target);
	const targetFile = file ?? selectedFile.value;
	if (!targetFile) return;
	if (targetFile !== selectedFile.value) await selectFile(targetFile, anchor);
	else {
		history.replaceState(null, "", `#help:${targetFile}${anchor ? "@" + anchor : ""}`);
		if (anchor) await scrollToAnchor(anchor);
	}
};

const selectFile = async (file: string, anchor: string | null = null) => {
	selectedFile.value = file;
	// URL odkaz na aktuálně otevřenou stránku nápovědy (#help:cesta/k/souboru.md) — jde o to samé
	// schéma, které App.vue zachytává na klik kdekoliv v aplikaci, takže adresu z prohlížeče (např.
	// po Ctrl+L Ctrl+C) lze poslat/uložit a otevře přesně tuhle stránku. replaceState místo
	// location.hash =, aby procházení stránkami nápovědy nezaplevelilo historii prohlížeče (tlačítko
	// zpět by jinak krokovalo jednotlivé stránky nápovědy místo návratu z aplikace).
	history.replaceState(null, "", `#help:${file}${anchor ? "@" + anchor : ""}`);
	const cached = pageCache.get(file);
	if (cached !== undefined) {
		html.value = cached;
		if (anchor) await scrollToAnchor(anchor);
		return;
	}
	loadingPage.value = true;
	try {
		const rendered = await helpTL.renderHelpPage(file);
		pageCache.set(file, rendered);
		html.value = rendered;
	} catch (e) {
		addFlash(formatTlError(e), "danger");
	} finally {
		loadingPage.value = false;
	}
	if (anchor) await scrollToAnchor(anchor);
};

// Markdown odkazy mezi stránkami nápovědy (např. index.md -> [Co všechno umím](skils.md)) se
// vykreslí jako obyčejné <a href="skils.md">, které by prohlížeč zkusil navigovat pryč ze SPA —
// zachytíme klik na odkaz končící ".md" (volitelně s "@kotvou") nebo začínající "@" (kotva na téže
// stránce) a přepneme stránku stejnou cestou jako klik v HelpMenuTree.vue. Jiné odkazy
// (http(s), mailto) projdou beze změny.
const onContentClick = (event: MouseEvent) => {
	const anchor = (event.target as HTMLElement).closest("a");
	if (!anchor) return;
	const href = anchor.getAttribute("href");
	if (!href) return;
	const { file } = parseTarget(href.split("?")[0]);
	if (file !== null && !file.endsWith(".md")) return;
	if (file === null && !href.startsWith("@")) return;

	event.preventDefault();
	openTarget(href);
};

onMounted(async () => {
	try {
		const index = await helpTL.getHelpIndex();
		content.value = index.content;
		// index.json řídí pořadí i výchozí stránku — otevírá se první položka v pořadí, jak ji autor
		// nápovědy zapsal (typicky "Novinky a změny"). Odkaz #help:soubor.md (viz App.vue) ale může
		// chtít otevřít help rovnou na konkrétní stránce — ta má přednost.
		const first = content.value[0];
		// initialFile může nést i kotvu (`soubor.md@kotva`)
		const initial = parseTarget(props.initialFile ?? first?.file ?? "");
		// layout (a tím i contentEl) se vykresluje až po loadingIndex=false — bez toho by se úvodní kotva
		// neměla kam posunout
		loadingIndex.value = false;
		if (initial.file) await selectFile(initial.file, initial.anchor);
	} catch (e) {
		addFlash(formatTlError(e), "danger");
	} finally {
		loadingIndex.value = false;
	}
});
</script>

<template>
	<div class="help-panel">
		<Loader v-if="loadingIndex" />
		<div v-else class="help-panel__layout">
			<nav class="help-panel__menu">
				<HelpMenuTree :content="content" :selected-file="selectedFile" @select="selectFile" />
			</nav>
			<div ref="contentEl" class="help-panel__content" @click="onContentClick">
				<Loader v-if="loadingPage" />
				<div v-else v-html="html" />
			</div>
		</div>
	</div>
</template>
